/**
 * Global post-mutation cache invalidation (shared by the axios + fetch
 * interceptors).
 *
 * After ANY successful write (POST/PUT/PATCH/DELETE) to our API we burst every
 * cache layer so lists/search reflect the change immediately AND a page refresh
 * can't be served stale data from persisted storage:
 *   1. the custom axios ApiCache (localStorage/sessionStorage/memory)
 *   2. the fetch-interceptor cache (separate in-memory map)
 *   3. React Query (refetches active queries, marks the rest stale)
 *
 * Two safeguards keep the blast radius sane:
 *   - a strict same-host `/api/` URL guard (no false positives on third-party
 *     URLs that merely contain "/api/" in a query string), and
 *   - a short trailing debounce so a burst of writes (e.g. a multi-file upload
 *     loop or a bulk action) collapses into a SINGLE invalidation instead of N.
 *
 * Invalidation is intentionally broad rather than per-resource: React Query
 * keys in this codebase are inconsistent and writes have cross-resource effects
 * (e.g. creating a property also creates a work-order ticket), so targeted
 * invalidation would silently miss lists. `invalidateQueries()` only refetches
 * *active* queries, so the steady-state cost is one refetch per write.
 */

import { API_BASE_URL } from "@/lib/config/api-base-url";

const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];
// Auth/session writes must NOT nuke the cache (e.g. silent token refresh).
const SKIP_PATHS = ['/token/refresh', '/auth/logout', '/admin-verify-code'];

const DEBOUNCE_MS = 300;

/**
 * True only for requests that actually hit our API. Matches on the URL's
 * pathname (not a naive substring) so third-party URLs with "/api/" buried in
 * a query string or path segment don't trigger a cache clear.
 */
export function isOurApiUrl(url?: string): boolean {
  if (!url) return false;
  if (API_BASE_URL && url.startsWith(API_BASE_URL)) return true;
  try {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(url, origin);
    if (API_BASE_URL) {
      try {
        const base = new URL(API_BASE_URL);
        if (parsed.host === base.host && parsed.pathname.startsWith('/api/')) {
          return true;
        }
      } catch {
        /* API_BASE_URL not absolute — fall through to same-origin check */
      }
    }
    return parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

/**
 * Whether a completed request should trigger cache invalidation.
 */
export function shouldInvalidateForMutation(method?: string, url?: string): boolean {
  const m = (method || 'get').toLowerCase();
  if (!MUTATING_METHODS.includes(m)) return false;
  if (!isOurApiUrl(url)) return false;
  if (url && SKIP_PATHS.some((path) => url.includes(path))) return false;
  return true;
}

let timer: ReturnType<typeof setTimeout> | null = null;

const flush = (): void => {
  timer = null;
  // Custom axios cache (all storages + memory).
  import('./api_utils')
    .then(({ invalidateCache }) => invalidateCache('*'))
    .catch(() => {});
  // Fetch-interceptor cache (separate in-memory map).
  import('./api-cache-interceptor')
    .then(({ cacheManager }) => cacheManager.clear())
    .catch(() => {});
  // React Query — refetches active queries, marks the rest stale.
  import('./query-client-registry')
    .then(({ getQueryClient }) => {
      getQueryClient()?.invalidateQueries();
    })
    .catch(() => {});
};

/**
 * Schedule a (debounced) invalidation of all cache layers. Repeated calls
 * within the debounce window coalesce into a single flush.
 */
export function scheduleCacheInvalidation(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(flush, DEBOUNCE_MS);
}
